import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Copy as Dup, Trash2, ShieldQuestion, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls } from "../../components/forms/FormBits";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import PermissionMatrix from "./PermissionMatrix";

export default function RolesPermissions({ catalog }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null); // {id?, name, description, permissions, is_system}

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/roles"); setRoles(data); }
    catch { toast.error("Failed to load roles"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (edit.id) await api.put(`/admin/roles/${edit.id}`, { name: edit.name, description: edit.description, permissions: edit.permissions });
      else await api.post("/admin/roles", { name: edit.name, description: edit.description, permissions: edit.permissions });
      toast.success("Role saved"); setEdit(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const dup = async (r) => { try { await api.post(`/admin/roles/${r.id}/duplicate`); toast.success("Role duplicated"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const del = async (r) => { if (!window.confirm(`Delete role "${r.name}"?`)) return; try { await api.delete(`/admin/roles/${r.id}`); toast.success("Role deleted"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div data-testid="roles-panel">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl text-navy">Roles &amp; Permissions</h2>
          <p className="mt-1 text-sm text-muted-foreground">Create unlimited reusable role templates with granular, per-module permissions.</p>
        </div>
        <button onClick={() => setEdit({ name: "", description: "", permissions: {} })} className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-semibold text-white hover:bg-gold-dark" data-testid="role-create-open">
          <Plus className="h-4 w-4" /> New Role
        </button>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {roles.map((r) => (
            <div key={r.id} className="rounded-2xl border border-border bg-white p-5" data-testid={`role-card-${r.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-serif text-lg text-navy">{r.name}</p>
                  {r.is_system && <span className="mt-1 inline-block rounded-full bg-navy/5 px-2 py-0.5 text-[10px] font-semibold text-navy">SYSTEM TEMPLATE</span>}
                </div>
                <span className="rounded-full bg-gold/10 px-2 py-0.5 text-[11px] font-semibold text-gold-dark">{r.user_count} user(s)</span>
              </div>
              <p className="mt-2 min-h-[40px] text-sm text-muted-foreground">{r.description}</p>
              <p className="mt-2 text-xs text-charcoal/60">{Object.keys(r.permissions || {}).length} module(s) permitted</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => setEdit({ ...r })} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-navy hover:border-navy" data-testid={`role-edit-${r.id}`}>Edit</button>
                <button onClick={() => dup(r)} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-navy hover:border-navy" data-testid={`role-dup-${r.id}`}><Dup className="h-3.5 w-3.5" /> Duplicate</button>
                {!r.is_system && <button onClick={() => del(r)} className="inline-flex items-center gap-1.5 rounded-lg border border-destructive/40 px-3 py-1.5 text-xs font-semibold text-destructive hover:bg-destructive/5" data-testid={`role-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /> Delete</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader><DialogTitle className="font-serif text-navy">{edit?.id ? (edit?.is_system ? "Edit System Template" : "Edit Role") : "New Role"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div><label className="mb-1 block text-sm font-semibold text-charcoal">Role name</label><input className={inputCls} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="role-form-name" /></div>
                <div><label className="mb-1 block text-sm font-semibold text-charcoal">Description</label><input className={inputCls} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} data-testid="role-form-desc" /></div>
              </div>
              <PermissionMatrix modules={catalog.modules} actions={catalog.actions} value={edit.permissions} onChange={(v) => setEdit({ ...edit, permissions: v })} />
              <div className="flex justify-end gap-3">
                <button onClick={() => setEdit(null)} className="rounded-lg border border-border px-5 py-2.5 text-sm font-semibold text-charcoal">Cancel</button>
                <button onClick={save} className="rounded-lg bg-navy px-6 py-2.5 text-sm font-semibold text-white hover:bg-navy-light" data-testid="role-form-save">Save Role</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
